
import React from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAiAssistant } from '@/contexts/AiAssistantContext';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';
import type { TopicType } from '@/types/assistant';

// Topic configuration for the cards
const TOPIC_CONFIG = {
  guide: {
    color: 'botscheme-royal-purple',
    gradientClass: 'from-botscheme-royal-purple/10 to-transparent',
    shadowClass: 'shadow-botscheme-royal-purple/10',
  },
  performance: {
    color: 'botscheme-ocean-blue',
    gradientClass: 'from-botscheme-ocean-blue/10 to-transparent',
    shadowClass: 'shadow-botscheme-ocean-blue/10',
  },
  trading_strategy: {
    color: 'botscheme-emerald-green',
    gradientClass: 'from-botscheme-emerald-green/10 to-transparent',
    shadowClass: 'shadow-botscheme-emerald-green/10',
  },
  advisor: {
    color: 'botscheme-amber-gold',
    gradientClass: 'from-botscheme-amber-gold/10 to-transparent',
    shadowClass: 'shadow-botscheme-amber-gold/10',
  }
};

export const EmptyState: React.FC = () => {
  const { createNewSession } = useAiAssistant();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const handleCreateSession = (topic: TopicType) => {
    createNewSession(topic);
  };

  // Animation variants for staggered animations
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: isMobile ? 0.05 : 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };
  
  return (
    <motion.div 
      className="flex flex-col items-center justify-center h-full p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="text-center mb-8">
        <motion.h1 
          className={cn("font-bold mb-2", isMobile ? "text-2xl" : "text-3xl")}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {t('ai_assistant.empty_state.title')}
        </motion.h1>
        <motion.p 
          className={cn("text-muted-foreground", isMobile && "text-sm")}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {t('ai_assistant.empty_state.subtitle')}
        </motion.p>
      </div>
      
      <motion.div 
        className={cn(
          "grid gap-4 w-full",
          isMobile ? "grid-cols-1 max-w-full" : "md:grid-cols-2 max-w-3xl"
        )}
        variants={container}
        initial="hidden"
        animate="show"
      >
        {Object.entries(TOPIC_CONFIG).map(([topic, config]) => (
          <motion.div key={topic} variants={item}>
            <Card 
              className={cn(
                "border border-white/10 bg-gradient-to-b backdrop-blur-sm hover:shadow-md transition-all",
                `${config.gradientClass}`,
                `hover:${config.shadowClass}`,
                isMobile && "p-1"
              )}
            >
              <CardHeader className={isMobile ? "py-3 px-3" : ""}>
                <CardTitle className={cn(
                  `text-${config.color}`,
                  isMobile ? "text-base" : "text-lg"
                )}>
                  {t(`ai_assistant.empty_state.${topic}.title`)}
                </CardTitle>
              </CardHeader>
              <CardContent className={isMobile ? "py-0 px-3" : ""}>
                <p className={cn(
                  "text-muted-foreground",
                  isMobile ? "text-xs" : "text-sm"
                )}>
                  {t(`ai_assistant.empty_state.${topic}.description`)}
                </p>
              </CardContent>
              <CardFooter className={isMobile ? "py-3 px-3" : ""}>
                <Button
                  variant="outline"
                  onClick={() => handleCreateSession(topic as TopicType)}
                  className={cn(
                    "w-full border-white/10 bg-white/5 backdrop-blur-sm",
                    `hover:bg-${config.color}/10 hover:border-${config.color}/20`,
                    `hover:text-${config.color}`,
                    isMobile && "text-xs py-1"
                  )}
                >
                  {t('ai_assistant.empty_state.button')}
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
};
